'use client';

import { Badge } from '@/components/ui/badge/badge';
import { Dialog } from '@/components/ui/dialog';
import { Image } from '@/components/ui/image/image';
import { MarketoForm } from '@/components/ui/marketo-form';
import { OPEN_MODAL_EVENT, type FormModalPayload } from '@/lib/utils/modal-event';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import styles from './form-modal.module.scss';

const FormModal = () => {
  const [open, setOpen] = useState(false);
  const [formContent, setFormContent] = useState<FormModalPayload | undefined>(undefined);
  const router = useRouter();
  const searchParams = useSearchParams();

  // Check if modal should be open based on URL params
  const contactFormParam = searchParams.get('contact-form');
  const stateParam = searchParams.get('state');

  // Open modal when form param exists
  useEffect(() => {
    if (contactFormParam && stateParam === 'open') {
      setOpen(true);
    }
  }, [contactFormParam, stateParam]);

  // Handle modal close with URL cleanup
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);

      if (!isOpen) {
        // Remove all tracking params when modal closes
        const params = new URLSearchParams(searchParams.toString());
        params.delete('contact-form');
        params.delete('state');
        params.delete('aliId'); // Also clean up success param
        params.delete('product');
        params.delete('cta');

        const newUrl = params.toString()
          ? `${window.location.pathname}?${params.toString()}`
          : window.location.pathname;
        router.replace(newUrl);

        // Clear formContent from state
        setFormContent(undefined);
      }
    },
    [router, searchParams],
  );

  // Listen for custom event and extract formContent from event detail
  useEffect(() => {
    const handleOpen = (event: Event) => {
      const customEvent = event as CustomEvent<FormModalPayload>;
      if (customEvent.detail?.formId) {
        setFormContent(customEvent.detail);
        setOpen(true);
      }
    };

    window.addEventListener(OPEN_MODAL_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_MODAL_EVENT, handleOpen);
  }, []);

  return (
    <Dialog
      className={styles.dialog}
      open={open}
      onOpenChange={handleOpenChange}
      modal
      closeOnOutsideClick={false}
      forceMount
    >
      <div className={styles.component}>
        <div className={styles.component_inner}>
          <div className={styles.component_left}>
            <div className={styles.component_left_head}>
              <Badge
                text={formContent?.badge || 'contact'}
                className={styles.component_left_head_badge}
              />
              <div className={styles.component_left_head_title}>
                <DialogPrimitive.Title asChild>
                  <h3 className="text-text-dark-purple">
                    {formContent?.title || 'Book a live demo today!'}
                  </h3>
                </DialogPrimitive.Title>
                <DialogPrimitive.Description
                  asChild
                  className={styles.component_left_head_title_subheading}
                >
                  <p>
                    {formContent?.description ||
                      'Streamline your supply chain process, reduce risk and find sub-contractors for your projects.'}
                  </p>
                </DialogPrimitive.Description>
              </div>
            </div>
            <Image
              className={styles.component_left_logo}
              src={'/images/logo.svg'}
              height={44}
              width={170}
              alt="logo"
            />
          </div>
          <div className={styles.component_right}>
            <MarketoForm formId={formContent?.formId} formContent={formContent} loadOnOpen={open} />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default FormModal;
